import { z } from 'zod';
import { i18nTextSchema } from './common.js';

/**
 * AI module contracts (CLAUDE.md §3 `ai` + §10). Everything funnels through
 * one gateway: entitlement check -> credit reservation -> async job -> usage
 * and cost logged. No feature calls a model directly.
 */

export const aiJobTypeSchema = z.enum([
  'facebook_import',
  'image_edit',
  'transcribe',
  'support_answer',
  'menu_import',
]);
export type AiJobType = z.infer<typeof aiJobTypeSchema>;

export const aiJobStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'cancelled']);

export const aiJobSchema = z.object({
  id: z.string().uuid(),
  type: aiJobTypeSchema,
  status: aiJobStatusSchema,
  credits: z.number().int(),
  error: z.string().nullable(),
  result: z.unknown().nullable(),
  createdAt: z.string().datetime(),
  finishedAt: z.string().datetime().nullable(),
});
export type AiJobDto = z.infer<typeof aiJobSchema>;

/* ------------------------------------------------------------------ */
/* Image editing (§10, ships first)                                    */
/* ------------------------------------------------------------------ */

export const imageEditPresetSchema = z.enum([
  'remove_background',
  'white_background',
  'brand_background',
  'lifestyle_studio',
  'lifestyle_outdoor',
  'cleanup',
]);
export type ImageEditPreset = z.infer<typeof imageEditPresetSchema>;

export const imageEditRequestSchema = z.object({
  productId: z.string().uuid().optional(),
  sourceUrl: z.string().url(),
  preset: imageEditPresetSchema,
  /** Only for `brand_background`; defaults to the shop's --color-primary. */
  backgroundHex: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
});

export const imageEditResultSchema = z.object({
  /** Originals are kept — the edit is non-destructive (§10). */
  originalUrl: z.string().url(),
  editedUrl: z.string().url(),
  preset: imageEditPresetSchema,
});

/* ------------------------------------------------------------------ */
/* Khmer voice transcription (§10, provider: Gemini)                   */
/* ------------------------------------------------------------------ */

export const transcribeRequestSchema = z.object({
  /** Telegram file id; the worker downloads the OGG/Opus and feeds it raw. */
  telegramFileId: z.string().max(200).optional(),
  audioUrl: z.string().url().optional(),
  durationSeconds: z.number().int().min(1).max(600),
  /** Ask the model to also pull order intent out of the same call. */
  extractOrderIntent: z.boolean().default(false),
});

export const orderIntentSchema = z.object({
  items: z.array(
    z.object({
      productHint: z.string().max(120),
      size: z.string().max(30).nullable(),
      color: z.string().max(30).nullable(),
      quantity: z.number().int().min(1).max(99).default(1),
    }),
  ),
  deliveryHint: z.string().max(200).nullable(),
  phoneHint: z.string().max(24).nullable(),
  confidence: z.number().min(0).max(1),
});
export type OrderIntent = z.infer<typeof orderIntentSchema>;

export const transcribeResultSchema = z.object({
  /** Ship as a verbatim transcript — Khmer quality is validated (§10). */
  transcript: z.string(),
  language: z.string().max(12).nullable(),
  intent: orderIntentSchema.nullable(),
});

/* ------------------------------------------------------------------ */
/* Support tier 2 (v2, but the contract is fixed now)                  */
/* ------------------------------------------------------------------ */

/**
 * Grounding is strict: the model answers only from this shop's catalog, this
 * customer's own orders, and the shop's policies. Low confidence escalates to
 * the merchant instead of guessing (§3 `support`).
 */
export const supportAnswerRequestSchema = z.object({
  question: z.string().min(1).max(1000),
  customerId: z.string().uuid().nullable(),
  locale: z.enum(['km', 'en']).default('km'),
});

export const supportAnswerSchema = z.object({
  answer: z.string(),
  confidence: z.number().min(0).max(1),
  /** Which shop facts the answer leaned on — shown to the merchant on escalation. */
  usedSources: z.array(z.string()).default([]),
  shouldEscalate: z.boolean(),
});

/* ------------------------------------------------------------------ */
/* Menu import (ships with the F&B vertical; pipeline is generic now)   */
/* ------------------------------------------------------------------ */

export const menuImportRequestSchema = z.object({
  imageUrls: z.array(z.string().url()).min(1).max(10),
});

export const menuItemDraftSchema = z.object({
  name: i18nTextSchema,
  priceAmount: z.number().int().min(0).nullable(),
  modifierGroups: z
    .array(
      z.object({
        name: i18nTextSchema,
        required: z.boolean(),
        options: z.array(z.object({ name: i18nTextSchema, priceDelta: z.number().int() })),
      }),
    )
    .default([]),
});
